// Storage key includes the schema version — bump CONSENT_SCHEMA_VERSION when adding categories.
// Records with a mismatched version are discarded and the user is re-prompted.
import { warnViaLogger } from '@/lib/lazyLogger'
import { safeLocalStorage } from '@/services/safeLocalStorage'

export const CONSENT_SCHEMA_VERSION = 1 as const

const STORAGE_KEY = `np_cookie_consent_v${CONSENT_SCHEMA_VERSION}`

export interface ConsentCategories {
  analytics: boolean
}

export const DEFAULT_CONSENT: ConsentCategories = { analytics: false }
export const ACCEPTED_CONSENT: ConsentCategories = { analytics: true }

interface StoredConsent extends ConsentCategories {
  version: typeof CONSENT_SCHEMA_VERSION
  updatedAt: string
}

export function getConsent(): ConsentCategories | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = safeLocalStorage.get(STORAGE_KEY)
    if (raw === null) return null

    const parsed = JSON.parse(raw) as StoredConsent
    if (parsed.version !== CONSENT_SCHEMA_VERSION) return null

    return { analytics: parsed.analytics }
  } catch (error) {
    warnViaLogger('Failed to read cookie consent from localStorage:', error)
    return null
  }
}

export function setConsent(cats: ConsentCategories): void {
  if (typeof window === 'undefined') return

  try {
    const stored: StoredConsent = {
      ...cats,
      version: CONSENT_SCHEMA_VERSION,
      updatedAt: new Date().toISOString(),
    }
    safeLocalStorage.set(STORAGE_KEY, JSON.stringify(stored))
  } catch (error) {
    warnViaLogger('Failed to save cookie consent to localStorage:', error)
  }
}

export function hasStoredConsent(): boolean {
  return getConsent() !== null
}

export function clearConsent(): void {
  if (typeof window === 'undefined') return

  try {
    safeLocalStorage.remove(STORAGE_KEY)
  } catch (error) {
    warnViaLogger('Failed to clear cookie consent from localStorage:', error)
  }
}

export function isAnalyticsGranted(): boolean {
  return getConsent()?.analytics === true
}
